/**
 * Chain layout algorithm for the Egyptian Domino 3D table.
 *
 * Pure TypeScript, no rendering: maps a {@link Chain} to tile centers +
 * rotations in table units. DETERMINISTIC pure function of
 * (chain, viewport aspect, table dimensions). No randomness, clocks, I/O,
 * three.js / React / Convex imports.
 *
 * Board model (spec §2.5, §8.4): placements[0] is the round's first tile
 * (side "center"), centered on the table; later placements name the open end
 * (`side`) they were added to. Left grows toward −x, right toward +x, no
 * spinner (exactly two open ends). The array is consumed in order as the
 * play sequence — a single left→right walk with interleaved ends is only
 * well-defined that way, and spatial ordering is ambiguous after turns.
 * Doubles ALWAYS lie screen-HORIZONTAL (house style), whatever the local run
 * direction: flat in a horizontal row, crosswise — centered on the column —
 * in a vertical drop. The junction walk stays uniform; only the rendered
 * orientation and the junction advance (a crosswise double is one tile wide
 * along a vertical run) depend on it. The round's OPENING tile keeps its own
 * crosswise anchor pose at the table center.
 *
 * Turn convention (ADR-0020 v6, "real-table flow"): rows run INLINE to the
 * felt boundary — a tile that fits flat stays flat (the corner reserve is a
 * single tile width, just enough for the turning tile itself; rows never
 * turn early). At the wall the end drops DOWN (+y) VERTICAL_RUN_LIMIT
 * tiles; the return row's first tile is CENTERED on the column axis — the
 * junction meets its long side at its center — half a width below, and the
 * run continues toward whichever horizontal direction still has room (the
 * pre-drop direction first). The two branches share the whole felt; their
 * rows sit at different heights after the first drop, so they only interact
 * in pathological full-table chains.
 *
 * Corners: a plain turning tile stands vertical, flush beyond the row end in
 * its outer-half column; a double turning down lies horizontal, tucked flush
 * under the end tile (centered on it) with the drop continuing single-file
 * from its bottom edge. At the bottom of a drop the return tile — plain or
 * double — is centered on the column. Doubles are always screen-horizontal
 * (flat in rows, crosswise in drops); only the OPENING tile keeps its
 * vertical anchor pose at the table center.
 *
 * Corner geometry: a 90° corner of full-width slabs cannot keep the junction
 * at the face center without overlap, so the turning tile is shifted by half
 * its perpendicular extent along the OLD travel direction; its inner face
 * still touches the junction (at the face endpoint). Straight runs keep the
 * junction at face centers; the center-line stays continuous across doubles.
 *
 * Limitations: ends never avoid each other; post-turn bounds containment is
 * best-effort; assumes a safe rect a few tile lengths across.
 */

import type { Chain, ChainPlacement } from "@domino/types";
import type {
  FitTransform,
  GhostPlacement,
  LayoutResult,
  TableDimensions,
  TilePlacement,
} from "./layoutTypes.js";

/** Tile length (long axis) in table units. Tiles are 2:1 slabs. */
export const TILE_LENGTH = 2.0;

/** Tile width (short axis) in table units. */
export const TILE_WIDTH = 1.0;

/** Vertical connector tiles per drop before the leg runs back. */
export const VERTICAL_RUN_LIMIT = 3;

/** Default lower clamp for the fit zoom. */
export const MIN_ZOOM = 0.05;

/** Default upper clamp for the fit zoom. */
export const MAX_ZOOM = 4.0;

/** Span substituted for a degenerate (zero-area) bounding box. */
const DEGENERATE_SPAN = 1e-6;

/** 2D point/vector in table units. */
interface Vec {
  x: number;
  y: number;
}

/** Axis-aligned bounding box in table units. */
interface Aabb {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Travel direction of an open end; turns are 90° so it stays axis-aligned. */
type AxisDirection = "posX" | "negX" | "posY" | "negY";

/** Sides that ever grow an end in this module ("center" is first-tile only). */
type LayoutSide = "left" | "right";

function directionToVec(direction: AxisDirection): Vec {
  switch (direction) {
    case "posX":
      return { x: 1, y: 0 };
    case "negX":
      return { x: -1, y: 0 };
    case "posY":
      return { x: 0, y: 1 };
    case "negY":
      return { x: 0, y: -1 };
  }
}

/** CCW rotation (radians) mapping the tile's length axis onto `direction`. */
function directionToRotation(direction: AxisDirection): number {
  switch (direction) {
    case "posX":
      return 0;
    case "posY":
      return Math.PI / 2;
    case "negX":
      return Math.PI;
    case "negY":
      return -Math.PI / 2;
  }
}

/** A placement is a double exactly when both halves are equal. */
function isDoublePlacement(placement: ChainPlacement): boolean {
  return placement.inwardHalf === placement.outwardHalf;
}

// ---------------------------------------------------------------------------
// End cursors: live position + direction of each open end while walking
// ---------------------------------------------------------------------------

/** Mutable walk state of one open end of the chain. */
interface EndCursor {
  /** Junction point (table units) where the next tile's inner face lands. */
  junction: Vec;
  /** Current direction of travel. */
  direction: AxisDirection;
  /** Boundary turns performed so far (reported as `turnsUsed`). */
  turns: number;
  /** Tiles still allowed in the current downward drop (after the corner). */
  verticalRemaining: number;
  /** True when the previous tile was a CROSSWISE double (hug/step-down rule). */
  crossTurnPending: boolean;
  /** Last horizontal direction before turning down (posX or negX). */
  lastHorizontalDir: "posX" | "negX";
}

/**
 * Footprint the tile would occupy with its inner face at `junction`,
 * travelling along `direction`. Straight tiles: non-doubles extend
 * TILE_LENGTH along travel (±TILE_WIDTH/2 across); doubles are crosswise
 * (TILE_WIDTH along travel, ±TILE_LENGTH/2 across). A TURNING tile passes
 * `shiftedAlongOld` (half its perpendicular extent along the old direction)
 * so the corner overlaps nothing while the inner face still touches the
 * junction.
 */
function footprintFor(
  junction: Vec,
  direction: AxisDirection,
  crosswise = false,
  shiftedAlongOld?: Vec,
): Aabb {
  const d = directionToVec(direction);
  // A crosswise double extends TILE_WIDTH along the travel and TILE_LENGTH
  // across it (any run direction).
  const travel = crosswise ? TILE_WIDTH : TILE_LENGTH;
  const across = (crosswise ? TILE_LENGTH : TILE_WIDTH) / 2;
  const shiftX = shiftedAlongOld !== undefined ? shiftedAlongOld.x : 0;
  const shiftY = shiftedAlongOld !== undefined ? shiftedAlongOld.y : 0;
  const nearX = junction.x;
  const nearY = junction.y;
  const farX = junction.x + d.x * travel;
  const farY = junction.y + d.y * travel;
  const offX = Math.abs(d.y) * across;
  const offY = Math.abs(d.x) * across;
  return {
    minX: Math.min(nearX, farX) - offX + shiftX,
    maxX: Math.max(nearX, farX) + offX + shiftX,
    minY: Math.min(nearY, farY) - offY + shiftY,
    maxY: Math.max(nearY, farY) + offY + shiftY,
  };
}

/** True when `box` lies fully inside the safe rect (edge contact counts). */
function fitsInSafeRect(box: Aabb, safe: Aabb): boolean {
  return (
    box.minX >= safe.minX && box.maxX <= safe.maxX && box.minY >= safe.minY && box.maxY <= safe.maxY
  );
}

/**
 * Clearance beyond a straight tile's far face needed for the NEXT tile to
 * turn down at that junction: the vertical corner tile — or a double tucked
 * under the row end — extends at most one tile WIDTH beyond the junction.
 * Deliberately minimal: rows must run flush to the felt edge and never turn
 * a tile early (user ruling, ADR-0020 v6).
 */
const CORNER_RESERVE = TILE_WIDTH;

/** True when a corner turned at this tile's far face would still fit. */
function hasCornerRoom(box: Aabb, direction: AxisDirection, safe: Aabb): boolean {
  switch (direction) {
    case "posX":
      return box.maxX + CORNER_RESERVE <= safe.maxX;
    case "negX":
      return box.minX - CORNER_RESERVE >= safe.minX;
    case "posY":
      return box.maxY + CORNER_RESERVE <= safe.maxY;
    case "negY":
      return box.minY - CORNER_RESERVE >= safe.minY;
  }
}

/**
 * Decide the travel direction for the next tile on `side`.
 *
 * Classic-line rule (ADR-0020 v4): keep extending the line INLINE — every
 * tile flat along the travel, doubles included — until the felt boundary,
 * then drop DOWN (+y) for a VERTICAL_RUN_LIMIT-tile connector and run back
 * the opposite way. Each branch keeps to its own half of the table. Also
 * mutates the cursor's bookkeeping (drop counter, turn count) so every
 * walker stays in sync.
 *
 * Corner shifts: the turning tile takes the end tile's outer-half column,
 * flush under it (classic L); the return row starts flush below the column
 * end. Every tile is flat along its travel, shifts included.
 */
function resolveDirection(
  cursor: EndCursor,
  isDouble: boolean,
  safe: Aabb,
): { direction: AxisDirection; shift?: Vec; crosswise: boolean } {
  // Currently moving vertically down ("posY") — the drop connector.
  if (cursor.direction === "posY") {
    cursor.verticalRemaining -= 1;
    if (cursor.verticalRemaining > 0 && cursor.junction.y + TILE_LENGTH <= safe.maxY) {
      return { direction: "posY", crosswise: isDouble };
    }

    // Drop finished (or floor reached). If it ended ON a crosswise double
    // (horizontal, spanning the column), step DOWN one more vertical tile
    // first — a horizontal return tile directly under it would touch it
    // parallel (the forbidden pairing).
    if (cursor.crossTurnPending && cursor.junction.y + TILE_LENGTH <= safe.maxY) {
      cursor.verticalRemaining = 1; // turn back right after the step-down
      return { direction: "posY", crosswise: false };
    }

    // Turn back: the return row starts CENTERED on the column axis — the
    // junction meets its long side at its center — half a width below the
    // column end. It continues toward whichever horizontal direction still
    // has room, preferring the pre-drop one.
    const prefer = cursor.lastHorizontalDir;
    const opposite: AxisDirection = prefer === "posX" ? "negX" : "posX";
    let chosen: AxisDirection = prefer;
    for (const dir of [prefer, opposite]) {
      const d = directionToVec(dir);
      const cont: Vec = {
        x: cursor.junction.x + d.x * TILE_LENGTH,
        y: cursor.junction.y + TILE_WIDTH / 2,
      };
      if (fitsInSafeRect(footprintFor(cont, dir), safe)) {
        chosen = dir;
        break;
      }
    }
    // advanceCursor composes center = junction + d*travel/2 + shift. Pin the
    // center to the column axis (junction.x), clamped into the safe rect so
    // a wall-hugging column never pushes the tile past the edge. A crosswise
    // return double travels TILE_WIDTH and hangs TILE_LENGTH tall, so it
    // starts a full width lower to clear the column's bottom edge.
    const travel = isDouble ? TILE_WIDTH : TILE_LENGTH;
    const target = Math.min(
      Math.max(cursor.junction.x, safe.minX + TILE_LENGTH / 2),
      safe.maxX - TILE_LENGTH / 2,
    );
    const newD = directionToVec(chosen);
    const shift: Vec = {
      x: target - (cursor.junction.x + newD.x * (travel / 2)),
      y: isDouble ? TILE_LENGTH / 2 : TILE_WIDTH / 2,
    };
    cursor.direction = chosen;
    cursor.lastHorizontalDir = chosen;
    cursor.turns += 1;
    cursor.verticalRemaining = 0;
    cursor.crossTurnPending = false;
    return { direction: chosen, shift, crosswise: isDouble };
  }

  // Currently moving horizontally ("posX" or "negX"): extend the line.
  const straight = footprintFor(cursor.junction, cursor.direction, isDouble);
  if (fitsInSafeRect(straight, safe) && hasCornerRoom(straight, cursor.direction, safe)) {
    return { direction: cursor.direction, crosswise: isDouble };
  }

  // Wall reached: turn DOWN (+y).
  // After a crosswise in-row double (vertical, spanning the line) the
  // turning tile hugs single-file BELOW it, centered on the double.
  // Otherwise: classic L — outer-half column flush beyond the row end.
  // The turning tile itself never plays crosswise (it aligns with the drop).
  const next: AxisDirection = "posY";
  const old = directionToVec(cursor.direction);
  const shift: Vec = cursor.crossTurnPending
    ? { x: (-old.x * TILE_WIDTH) / 2, y: TILE_LENGTH / 2 }
    : { x: (old.x * TILE_WIDTH) / 2, y: 0 };
  cursor.direction = next;
  cursor.turns += 1;
  cursor.verticalRemaining = VERTICAL_RUN_LIMIT;
  cursor.crossTurnPending = false;
  return { direction: next, shift, crosswise: false };
}

/**
 * Place one non-first tile: derive center/rotation and advance the cursor's
 * junction to the midpoint of the new outer face. Doubles always render
 * horizontal: flat along a horizontal run, crosswise (centered on the
 * column) in a vertical drop — where the junction advances only TILE_WIDTH,
 * from the double's inner long side to its outer one. The matching (inner)
 * half faces the junction by construction.
 */
function advanceCursor(
  cursor: EndCursor,
  placement: TilePlacement,
  direction: AxisDirection,
  crosswise = false,
  shift?: Vec,
): void {
  const d = directionToVec(direction);
  const travel = crosswise ? TILE_WIDTH : TILE_LENGTH;
  const shiftX = shift !== undefined ? shift.x : 0;
  const shiftY = shift !== undefined ? shift.y : 0;
  placement.x = cursor.junction.x + d.x * (travel / 2) + shiftX;
  placement.y = cursor.junction.y + d.y * (travel / 2) + shiftY;
  placement.rotation = directionToRotation(direction) + (crosswise ? Math.PI / 2 : 0);
  placement.width = TILE_LENGTH;
  placement.height = TILE_WIDTH;
  cursor.junction = {
    x: placement.x + d.x * (travel / 2),
    y: placement.y + d.y * (travel / 2),
  };
  cursor.crossTurnPending = crosswise;
}

/** Grow `bounds` to include the rotated footprint of `placement`. */
function includePlacement(bounds: Aabb, placement: TilePlacement): void {
  const cos = Math.abs(Math.cos(placement.rotation));
  const sin = Math.abs(Math.sin(placement.rotation));
  const halfW = (placement.width * cos + placement.height * sin) / 2;
  const halfH = (placement.width * sin + placement.height * cos) / 2;
  bounds.minX = Math.min(bounds.minX, placement.x - halfW);
  bounds.maxX = Math.max(bounds.maxX, placement.x + halfW);
  bounds.minY = Math.min(bounds.minY, placement.y - halfH);
  bounds.maxY = Math.max(bounds.maxY, placement.y + halfH);
}

/** Validate table dimensions; returns the safe rect. Throws RangeError. */
function safeRectFor(table: TableDimensions): Aabb {
  const values: Record<string, number> = {
    "table.width": table.width,
    "table.height": table.height,
    "table.margin": table.margin,
  };
  for (const name of Object.keys(values)) {
    const value = values[name];
    if (value === undefined || !Number.isFinite(value) || value < 0) {
      throw new RangeError(`layout: ${name} must be a finite non-negative number`);
    }
  }
  if (table.width <= 0 || table.height <= 0) {
    throw new RangeError("layout: table.width and table.height must be positive");
  }
  if (table.margin * 2 >= Math.min(table.width, table.height)) {
    throw new RangeError("layout: table.margin must leave a non-empty safe rectangle");
  }
  return {
    minX: table.margin,
    minY: table.margin,
    maxX: table.width - table.margin,
    maxY: table.height - table.margin,
  };
}

/** Validate viewport size. Throws RangeError. */
function validateViewport(viewport: { width: number; height: number }): void {
  if (!Number.isFinite(viewport.width) || viewport.width <= 0) {
    throw new RangeError("layout: viewport.width must be a finite positive number");
  }
  if (!Number.isFinite(viewport.height) || viewport.height <= 0) {
    throw new RangeError("layout: viewport.height must be a finite positive number");
  }
}

// ---------------------------------------------------------------------------
// computeChainLayout
// ---------------------------------------------------------------------------

/**
 * Lay out a full chain in table units.
 *
 * Walk: the first placement is centered on the table (non-double along x,
 * double crosswise); each later placement appends to the end named by its
 * `side` ("center" anywhere but first falls back to the right end). Ends
 * turn 90° on safe-rectangle boundary hits, per the module-header convention.
 *
 * Deterministic: identical (chain, viewport, table) inputs give identical
 * output. The function never mutates its inputs.
 *
 * @param chain Chain to lay out (placements consumed in order as the play sequence).
 * @param viewport Viewport size in pixels (only the aspect/sample size frame the fit).
 * @param table Table dimensions (center + safe rect derive from these).
 */
export function computeChainLayout(
  chain: Chain,
  viewport: { width: number; height: number },
  table: TableDimensions,
  zoomBounds?: { minZoom: number; maxZoom: number },
): LayoutResult {
  validateViewport(viewport);
  const minZ = zoomBounds?.minZoom ?? MIN_ZOOM;
  const maxZ = zoomBounds?.maxZoom ?? MAX_ZOOM;
  const safe = safeRectFor(table);
  const cx = table.width / 2;
  const cy = table.height / 2;

  const placements: TilePlacement[] = chain.placements.map(() => ({
    x: 0,
    y: 0,
    rotation: 0,
    width: TILE_LENGTH,
    height: TILE_WIDTH,
  }));
  const bounds: Aabb = { minX: cx, minY: cy, maxX: cx, maxY: cy };

  const centerIdx = chain.placements.findIndex((p) => p.side === "center");
  if (centerIdx === -1) {
    // Empty chain: degenerate bounds at the table center; fit zooms in fully.
    const fit = computeFitTransform(bounds, viewport, minZ, maxZ);
    return {
      placements,
      turnsUsed: { left: 0, right: 0 },
      scale: fit.zoom,
      fit,
      bounds: { minX: cx, minY: cy, maxX: cx, maxY: cy },
    };
  }

  // Center opening tile: centered on the table.
  const centerEntry = chain.placements[centerIdx];
  const centerIsDouble = isDoublePlacement(centerEntry);
  const centerOut: TilePlacement = {
    x: cx,
    y: cy,
    rotation: centerIsDouble ? Math.PI / 2 : 0,
    width: TILE_LENGTH,
    height: TILE_WIDTH,
  };
  placements[centerIdx] = centerOut;
  includePlacement(bounds, centerOut);

  // Junctions sit on the outer faces of the center tile
  const firstAdvance = centerIsDouble ? TILE_WIDTH : TILE_LENGTH;
  const halfAdvance = firstAdvance / 2;
  const cursors: Record<LayoutSide, EndCursor> = {
    left: {
      junction: { x: cx - halfAdvance, y: cy },
      direction: "negX",
      turns: 0,
      verticalRemaining: 0,
      crossTurnPending: false,
      lastHorizontalDir: "negX",
    },
    right: {
      junction: { x: cx + halfAdvance, y: cy },
      direction: "posX",
      turns: 0,
      verticalRemaining: 0,
      crossTurnPending: false,
      lastHorizontalDir: "posX",
    },
  };

  // Walk left placements outward from center: centerIdx - 1 down to 0
  for (let i = centerIdx - 1; i >= 0; i--) {
    const cursor = cursors.left;
    const out = placements[i];
    const entry = chain.placements[i];
    const doubled = isDoublePlacement(entry);
    const resolved = resolveDirection(cursor, doubled, safe);
    advanceCursor(cursor, out, resolved.direction, resolved.crosswise, resolved.shift);
    includePlacement(bounds, out);
  }

  // Walk right placements outward from center: centerIdx + 1 to length - 1
  for (let i = centerIdx + 1; i < chain.placements.length; i++) {
    const cursor = cursors.right;
    const out = placements[i];
    const entry = chain.placements[i];
    const doubled = isDoublePlacement(entry);
    const resolved = resolveDirection(cursor, doubled, safe);
    advanceCursor(cursor, out, resolved.direction, resolved.crosswise, resolved.shift);
    includePlacement(bounds, out);
  }

  const fit = computeFitTransform(bounds, viewport, minZ, maxZ);
  return {
    placements,
    turnsUsed: { left: cursors.left.turns, right: cursors.right.turns },
    scale: fit.zoom,
    fit,
    bounds: { minX: bounds.minX, minY: bounds.minY, maxX: bounds.maxX, maxY: bounds.maxY },
  };
}

// ---------------------------------------------------------------------------
// computeFitTransform
// ---------------------------------------------------------------------------

/**
 * Fit an axis-aligned bounding box (table units) into a viewport (pixels)
 * with a "contain" zoom, clamped to [`minZoom`, `maxZoom`], and centered via
 * the offsets. The returned transform maps `screen = world * zoom + offset`.
 *
 * Deterministic pure function. Degenerate (zero-area) boxes zoom to
 * `maxZoom` centered on the box point.
 */
export function computeFitTransform(
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  viewport: { width: number; height: number },
  minZoom: number,
  maxZoom: number,
): FitTransform {
  if (!Number.isFinite(viewport.width) || viewport.width <= 0) {
    throw new RangeError("computeFitTransform: viewport.width must be positive");
  }
  if (!Number.isFinite(viewport.height) || viewport.height <= 0) {
    throw new RangeError("computeFitTransform: viewport.height must be positive");
  }
  if (!Number.isFinite(minZoom) || !Number.isFinite(maxZoom) || minZoom <= 0) {
    throw new RangeError("computeFitTransform: zoom bounds must be finite, minZoom > 0");
  }
  if (maxZoom < minZoom) {
    throw new RangeError("computeFitTransform: maxZoom must be >= minZoom");
  }
  const spanX = Math.max(bounds.maxX - bounds.minX, DEGENERATE_SPAN);
  const spanY = Math.max(bounds.maxY - bounds.minY, DEGENERATE_SPAN);
  const containZoom = Math.min(viewport.width / spanX, viewport.height / spanY);
  const zoom = Math.min(maxZoom, Math.max(minZoom, containZoom));
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;
  return {
    zoom,
    offsetX: viewport.width / 2 - centerX * zoom,
    offsetY: viewport.height / 2 - centerY * zoom,
  };
}

// ---------------------------------------------------------------------------
// computeAnchoredFitTransform
// ---------------------------------------------------------------------------

/**
 * Fit a bounding box into a viewport while pinning `anchor` (table units) to
 * the EXACT horizontal viewport center. Used to keep the round's opening
 * tile — which always sits at the table center in world space — fixed at the
 * middle of the felt while the two arms grow outward, so placing new tiles
 * never slides the chain sideways.
 *
 * Zoom is a "contain" fit, clamped to [`minZoom`, `maxZoom`]:
 * - X: the anchor is pinned, so each side may use half the viewport.
 * - Y: fitted over the full height, and the offset is the closest feasible
 *   point to pinning the anchor vertically — arms only grow downward after a
 *   corner turn, and when one end descends the view recenters vertically
 *   instead of letting tiles spill off the felt. For a vertically symmetric
 *   box this still pins the anchor exactly, and for an anchor at the box
 *   center the whole transform is identical to {@link computeFitTransform}.
 *
 * Deterministic pure function. `screen = world * zoom + offset`.
 */
export function computeAnchoredFitTransform(
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  anchor: { x: number; y: number },
  viewport: { width: number; height: number },
  minZoom: number,
  maxZoom: number,
): FitTransform {
  if (!Number.isFinite(viewport.width) || viewport.width <= 0) {
    throw new RangeError("computeAnchoredFitTransform: viewport.width must be positive");
  }
  if (!Number.isFinite(viewport.height) || viewport.height <= 0) {
    throw new RangeError("computeAnchoredFitTransform: viewport.height must be positive");
  }
  if (!Number.isFinite(minZoom) || !Number.isFinite(maxZoom) || minZoom <= 0) {
    throw new RangeError("computeAnchoredFitTransform: zoom bounds must be finite, minZoom > 0");
  }
  if (maxZoom < minZoom) {
    throw new RangeError("computeAnchoredFitTransform: maxZoom must be >= minZoom");
  }
  const spanX = Math.max(bounds.maxX - bounds.minX, DEGENERATE_SPAN);
  const spanY = Math.max(bounds.maxY - bounds.minY, DEGENERATE_SPAN);
  const halfW = viewport.width / 2;
  const halfH = viewport.height / 2;
  // Slide-first, zoom-last: the box only has to fit the viewport SOMEWHERE,
  // not with the anchor held mid-screen. The anchor keeps its centered pin
  // while that pin keeps both ends visible, then slides just enough to hold
  // the overflowing end on the felt; zooming out is the last resort. A
  // one-arm chain therefore keeps up to twice the zoom the old
  // anchor-distance zoom demanded (which wasted the short half of the felt).
  const zoomX = viewport.width / spanX;
  const zoomY = viewport.height / spanY;
  const zoom = Math.min(maxZoom, Math.max(minZoom, Math.min(zoomX, zoomY)));
  const pinX = halfW - anchor.x * zoom;
  const loX = -bounds.minX * zoom;
  const hiX = viewport.width - bounds.maxX * zoom;
  const offsetX =
    loX <= hiX
      ? Math.min(Math.max(pinX, loX), hiX)
      : halfW - ((bounds.minX + bounds.maxX) / 2) * zoom;
  // Y offset: pin the anchor vertically when the content allows, else clamp
  // into the feasible range; if the content is taller than the viewport at
  // this zoom, center it (best possible at the zoom floor).
  const pinY = halfH - anchor.y * zoom;
  const lo = -bounds.minY * zoom;
  const hi = viewport.height - bounds.maxY * zoom;
  const offsetY =
    lo <= hi ? Math.min(Math.max(pinY, lo), hi) : halfH - ((bounds.minY + bounds.maxY) / 2) * zoom;
  return {
    zoom,
    offsetX,
    offsetY,
  };
}

// ---------------------------------------------------------------------------
// computeGhostPlacement
// ---------------------------------------------------------------------------

/**
 * Replay the chain walk and return the live end cursors (junction +
 * direction + turns) for both ends. Shared by the layout and the ghost
 * preview so both obey identical turn rules. Returns `null` cursors when the
 * chain is empty.
 */
function walkEndCursors(
  chain: Chain,
  table: TableDimensions,
): { left: EndCursor | null; right: EndCursor | null; safe: Aabb; center: Vec } {
  const safe = safeRectFor(table);
  const center: Vec = { x: table.width / 2, y: table.height / 2 };
  const centerIdx = chain.placements.findIndex((p) => p.side === "center");
  if (centerIdx === -1) {
    return { left: null, right: null, safe, center };
  }
  const centerEntry = chain.placements[centerIdx];
  const firstAdvance = (isDoublePlacement(centerEntry) ? TILE_WIDTH : TILE_LENGTH) / 2;
  const cursors: { left: EndCursor; right: EndCursor } = {
    left: {
      junction: { x: center.x - firstAdvance, y: center.y },
      direction: "negX",
      turns: 0,
      verticalRemaining: 0,
      crossTurnPending: false,
      lastHorizontalDir: "negX",
    },
    right: {
      junction: { x: center.x + firstAdvance, y: center.y },
      direction: "posX",
      turns: 0,
      verticalRemaining: 0,
      crossTurnPending: false,
      lastHorizontalDir: "posX",
    },
  };

  // Walk left placements outward from center: centerIdx - 1 down to 0
  for (let i = centerIdx - 1; i >= 0; i--) {
    const cursor = cursors.left;
    const entry = chain.placements[i];
    const doubled = isDoublePlacement(entry);
    const resolved = resolveDirection(cursor, doubled, safe);
    const d = directionToVec(resolved.direction);
    const travel = resolved.crosswise ? TILE_WIDTH : TILE_LENGTH;
    const shiftX = resolved.shift !== undefined ? resolved.shift.x : 0;
    const shiftY = resolved.shift !== undefined ? resolved.shift.y : 0;
    cursor.junction = {
      x: cursor.junction.x + d.x * travel + shiftX,
      y: cursor.junction.y + d.y * travel + shiftY,
    };
    cursor.crossTurnPending = resolved.crosswise;
  }

  // Walk right placements outward from center: centerIdx + 1 to length - 1
  for (let i = centerIdx + 1; i < chain.placements.length; i++) {
    const cursor = cursors.right;
    const entry = chain.placements[i];
    const doubled = isDoublePlacement(entry);
    const resolved = resolveDirection(cursor, doubled, safe);
    const d = directionToVec(resolved.direction);
    const travel = resolved.crosswise ? TILE_WIDTH : TILE_LENGTH;
    const shiftX = resolved.shift !== undefined ? resolved.shift.x : 0;
    const shiftY = resolved.shift !== undefined ? resolved.shift.y : 0;
    cursor.junction = {
      x: cursor.junction.x + d.x * travel + shiftX,
      y: cursor.junction.y + d.y * travel + shiftY,
    };
    cursor.crossTurnPending = resolved.crosswise;
  }

  return { left: cursors.left, right: cursors.right, safe, center };
}

/**
 * Preview where `tile` would land if played on `end` right now.
 *
 * The ghost obeys the exact same walking + turn rules as
 * {@link computeChainLayout} (same junction, same direction, same turn
 * decision — including the corner shift), and its matching half faces the
 * junction: non-doubles lie with the inner end toward the chain, doubles sit
 * crosswise with the junction on their inner long side.
 *
 * NOTE on the signature: turn decisions depend on the safe rectangle, so the
 * table dimensions are a required parameter. Without them the preview could
 * not match the real layout; a required argument keeps the behaviour
 * explicit and deterministic.
 *
 * Empty chain: the ghost is the centered first tile (non-double along x,
 * double crosswise), returned on the requested side. `side: "center"` is
 * accepted for symmetry and behaves like the first tile.
 */
export function computeGhostPlacement(
  chain: Chain,
  end: "left" | "right" | "center",
  tile: { high: number; low: number; isDouble: boolean },
  table: TableDimensions,
): GhostPlacement {
  const side: LayoutSide = end === "left" ? "left" : end === "right" ? "right" : "right";
  const { left, right, safe, center } = walkEndCursors(chain, table);
  const ghost: TilePlacement = {
    x: 0,
    y: 0,
    rotation: 0,
    width: TILE_LENGTH,
    height: TILE_WIDTH,
  };

  const cursor = side === "left" ? left : right;
  if (cursor === null) {
    // Empty chain: ghost is the centered opener.
    ghost.x = center.x;
    ghost.y = center.y;
    ghost.rotation = tile.isDouble ? Math.PI / 2 : 0;
    return side === "left" ? { left: ghost } : { right: ghost };
  }
  const resolved = resolveDirection(cursor, tile.isDouble, safe);
  const scratch: EndCursor = {
    junction: { x: cursor.junction.x, y: cursor.junction.y },
    direction: resolved.direction,
    turns: 0,
    verticalRemaining: cursor.verticalRemaining,
    crossTurnPending: cursor.crossTurnPending,
    lastHorizontalDir: cursor.lastHorizontalDir,
  };
  advanceCursor(scratch, ghost, resolved.direction, resolved.crosswise, resolved.shift);
  // NOTE: tile.high/low identify the tile for the caller; orientation is never
  // a player choice (spec §2.6) — the matching half faces the junction by
  // construction of advanceCursor, so the pip values need no further use here.
  return side === "left" ? { left: ghost } : { right: ghost };
}
